/**
 * ATT-2871 admin half — tenant settings store (Zustand).
 *
 * Per design D-store: hydrated synchronously in `pages/_app.tsx` from bundled
 * config BEFORE any children render. The `useTenantSettings()` hook updates
 * the store via `setFromBackend()` when the backend response is parsed.
 *
 * Race-free guarantee: `tenantHasFlag()` reads via `getState()` and ALWAYS sees
 * a valid initial state (bundled values written by `hydrate.ts`).
 *
 * Caveat: `tenantHasFlag()` is a one-shot synchronous read — components that
 * need to react to backend updates must use `useTenantSettings()` hook + the
 * store subscription (via `useTenantSettingsStore(...)`).
 */

import { create } from "zustand";

export type TenantSettingsSource = "bundled" | "cache" | "backend";

export type TenantSettingsState = {
  source: TenantSettingsSource;
  featureFlags: string[];
  logoUrl: string | null;
  setBundled: (s: { featureFlags: string[]; logoUrl: string | null }) => void;
  setFromCache: (s: Partial<Pick<TenantSettingsState, "featureFlags" | "logoUrl">>) => void;
  setFromBackend: (s: Partial<Pick<TenantSettingsState, "featureFlags" | "logoUrl">>) => void;
};

export const useTenantSettingsStore = create<TenantSettingsState>((set) => ({
  source: "bundled",
  featureFlags: [],
  logoUrl: null,
  setBundled: (s) => set({ source: "bundled", ...s }),
  setFromCache: (s) => set((prev) => ({ ...prev, ...s, source: "cache" })),
  setFromBackend: (s) => set((prev) => ({ ...prev, ...s, source: "backend" })),
}));
